// createApiFetch — token-injecting fetch wrapper, framework-agnostic.
//
// Doesn't import Firebase directly so apps/mobile can swap in
// `@react-native-firebase/auth` later without touching this package.
// Callers pass in a `getToken` callback; how the token is sourced is
// the consumer's problem (web: getAuth().currentUser.getIdToken(),
// mobile: equivalent on RN Firebase).
//
// 401 handling is a single optional callback. The hook the app wires in
// usually does: signOut() → navigate to onboarding. We intentionally do
// NOT auto-retry; if the server says 401, the user is gone and the app
// has work to do (clear caches, drop the route).

export interface CreateApiFetchOptions {
  /**
   * Prefixed in front of paths that don't start with `http`. Empty string
   * keeps requests same-origin (correct for prod behind the Vercel rewrite).
   */
  baseUrl?: string;
  /**
   * Returns the current ID token, or `null` when the user is signed out.
   * Called on every request — implementations should cache internally
   * (Firebase already does this; it only hits the network when the token
   * is near-expiry).
   */
  getToken: () => Promise<string | null>;
  /**
   * Fired when the server returns 401. Use it to sign out + redirect.
   * Optional — omit when the surface doesn't need an auth gate (e.g.
   * scripts, smoke tests).
   */
  on401?: () => void;
}

export type ApiFetch = (path: string, init?: RequestInit) => Promise<Response>;

export function createApiFetch(options: CreateApiFetchOptions): ApiFetch {
  const baseUrl = options.baseUrl ?? '';

  return async function apiFetch(path, init = {}) {
    const headers = new Headers(init.headers);

    const token = await options.getToken();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    const url = path.startsWith('http') ? path : `${baseUrl}${path}`;
    const res = await fetch(url, { ...init, headers });

    if (res.status === 401 && options.on401) {
      options.on401();
    }
    return res;
  };
}
